const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const API_ERROR_WRAPPER_KEYS = ['error', 'response', 'data'] as const;
const MAX_API_ERROR_OBJECTS = 16;

const hasApiErrorValue = (
  error: unknown,
  matches: (value: Record<string, unknown>) => boolean,
): boolean => {
  const pending: unknown[] = [error];
  const visited = new Set<object>();

  while (pending.length > 0 && visited.size < MAX_API_ERROR_OBJECTS) {
    const current = pending.pop();

    if (!isRecord(current) || visited.has(current)) {
      continue;
    }

    visited.add(current);

    if (matches(current)) {
      return true;
    }

    for (const key of API_ERROR_WRAPPER_KEYS) {
      pending.push(current[key]);
    }
  }

  return false;
};

export const getApiErrorMessage = (error: unknown): string | null => {
  const visited = new Set<object>();

  const visit = (value: unknown): string | null => {
    if (!isRecord(value) || visited.has(value) || visited.size >= MAX_API_ERROR_OBJECTS) {
      return null;
    }

    visited.add(value);

    for (const key of API_ERROR_WRAPPER_KEYS) {
      const message = visit(value[key]);

      if (message !== null) {
        return message;
      }
    }

    if (value instanceof Error || typeof value.message !== 'string') {
      return null;
    }

    const message = value.message.trim();

    return message.length > 0 ? message : null;
  };

  return visit(error);
};

export const hasApiErrorCode = (error: unknown, code: string): boolean =>
  hasApiErrorValue(error, (value) => value.code === code);

export const hasApiErrorStatus = (error: unknown, statusCode: number): boolean =>
  hasApiErrorValue(
    error,
    (value) => value.status === statusCode || value.statusCode === statusCode,
  );
